import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Check, X, RefreshCw, Settings2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/platform`;

const humanizeFeature = (key) => key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function FeatureMatrixPanel({ onOpenEditor }) {
  const [matrix, setMatrix] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const response = await axios.get(`${API}/companies/features-matrix`);
      setMatrix(response.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Feature matrix could not be loaded.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRefresh = () => {
    setRefreshing(true);
    load();
  };

  if (loading) {
    return (
      <section data-testid="feature-matrix-loading" style={{ padding: "1.25rem", color: "#6b7280", fontSize: "0.85rem" }}>
        Loading feature matrix…
      </section>
    );
  }

  if (error) {
    return (
      <section
        data-testid="feature-matrix-error"
        style={{ padding: "1rem", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "10px", color: "#991b1b", fontSize: "0.85rem" }}
      >
        {error}
      </section>
    );
  }

  const featureKeys = matrix?.feature_keys || [];
  const rows = matrix?.rows || [];
  const offCounts = matrix?.off_counts || {};
  const labelMap = Object.fromEntries(
    (matrix?.feature_catalog || []).map(({ key, label }) => [key, label]),
  );
  const labelFor = (key) => labelMap[key] || humanizeFeature(key);

  return (
    <section data-testid="feature-matrix-panel" style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: "1rem", fontWeight: 600, color: "#111827" }}>Feature matrix</h2>
          <p style={{ margin: "0.15rem 0 0", fontSize: "0.8rem", color: "#6b7280" }}>
            {rows.length} {rows.length === 1 ? "company" : "companies"} · Green = on, red = off. Click any cell to open that company&rsquo;s editor.
          </p>
        </div>
        <button
          type="button"
          data-testid="feature-matrix-refresh"
          onClick={handleRefresh}
          disabled={refreshing}
          style={{
            display: "inline-flex", alignItems: "center", gap: "0.35rem",
            background: "transparent", border: "1px solid #d1d5db", borderRadius: "999px",
            padding: "0.3rem 0.8rem", fontSize: "0.8rem", color: "#374151",
            cursor: refreshing ? "wait" : "pointer",
          }}
        >
          <RefreshCw size={13} style={{ opacity: refreshing ? 0.5 : 1 }} />
          Refresh
        </button>
      </header>

      <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: "12px", background: "#fff" }}>
        <table data-testid="feature-matrix-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
          <thead>
            <tr style={{ background: "#f9fafb" }}>
              <th style={thLeft}>Company</th>
              {featureKeys.map((key) => (
                <th key={key} style={thCenter} data-testid={`feature-matrix-header-${key}`}>
                  <div>{labelFor(key)}</div>
                  <div
                    style={{
                      marginTop: "0.15rem", fontSize: "0.7rem", fontWeight: 500,
                      color: (offCounts[key] || 0) > 0 ? "#b91c1c" : "#6b7280",
                    }}
                  >
                    {offCounts[key] || 0} off
                  </div>
                </th>
              ))}
              <th style={thCenter}>Disabled</th>
              <th style={thCenter} aria-label="actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.company_id}
                data-testid={`feature-matrix-row-${row.company_id}`}
                style={{ borderTop: "1px solid #e5e7eb" }}
              >
                <td style={tdLeft}>
                  <div style={{ fontWeight: 600, color: "#111827" }}>{row.display_name || row.name || row.company_id}</div>
                  {row.status && row.status !== "active" && (
                    <div style={{ fontSize: "0.7rem", color: "#b45309", fontWeight: 500 }}>{row.status}</div>
                  )}
                </td>
                {featureKeys.map((key) => {
                  const on = !!row.features?.[key];
                  return (
                    <td key={key} style={tdCenter}>
                      <button
                        type="button"
                        title={`${labelFor(key)} · ${on ? "ON" : "OFF"} — click to open editor`}
                        onClick={() => onOpenEditor && onOpenEditor({ id: row.company_id, name: row.name, display_name: row.display_name })}
                        data-testid={`feature-matrix-cell-${row.company_id}-${key}`}
                        style={{
                          ...cellPill,
                          background: on ? "#ecfdf5" : "#fef2f2",
                          borderColor: on ? "#a7f3d0" : "#fecaca",
                          color: on ? "#047857" : "#b91c1c",
                        }}
                      >
                        {on ? <Check size={13} /> : <X size={13} />}
                      </button>
                    </td>
                  );
                })}
                <td style={{ ...tdCenter, fontWeight: 600, color: row.disabled_count > 0 ? "#b91c1c" : "#6b7280" }}>
                  {row.disabled_count}
                </td>
                <td style={tdCenter}>
                  <button
                    type="button"
                    data-testid={`feature-matrix-manage-${row.company_id}`}
                    onClick={() => onOpenEditor && onOpenEditor({ id: row.company_id, name: row.name, display_name: row.display_name })}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: "0.3rem",
                      background: "transparent", border: "1px solid #d1d5db", borderRadius: "999px",
                      padding: "0.25rem 0.7rem", fontSize: "0.75rem", color: "#374151", cursor: "pointer",
                    }}
                  >
                    <Settings2 size={12} /> Manage
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td style={{ ...tdLeft, textAlign: "center", color: "#6b7280", padding: "1.5rem" }} colSpan={featureKeys.length + 3}>
                  No companies to display.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const thLeft = {
  padding: "0.6rem 0.85rem",
  textAlign: "left",
  fontSize: "0.72rem",
  fontWeight: 600,
  color: "#374151",
  textTransform: "uppercase",
  letterSpacing: "0.03em",
};

const thCenter = { ...thLeft, textAlign: "center" };

const tdLeft = {
  padding: "0.55rem 0.85rem",
  textAlign: "left",
  verticalAlign: "middle",
  color: "#111827",
};

const tdCenter = { ...tdLeft, textAlign: "center" };

const cellPill = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "28px",
  height: "28px",
  border: "1px solid",
  borderRadius: "999px",
  cursor: "pointer",
};
